/**
 * Face tracking model loader.
 *
 * Goal: Prefer MediaPipe Tasks FaceLandmarker (WASM) for runtime stability/perf,
 * while keeping a TFJS fallback for environments where Tasks fails to init.
 *
 * Returned object matches the minimal TFJS FaceMesh surface used by this app:
 * `estimateFaces({ input, predictIrises }) -> [{ scaledMesh: number[][] }]`
 */

const DEFAULT_TASKS_VISION_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.32/wasm';

// Official hosted model (float16 / v1)
const DEFAULT_FACE_LANDMARKER_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

function toScaledMeshFromNormalizedLandmarks(faceLandmarks, videoW, videoH) {
  // MediaPipe returns normalized coords (x,y,z) in [0..1] for x/y.
  // We convert to TFJS-like `scaledMesh` (pixel x/y, z in pixel-ish scale).
  const zScale = videoW || 1;
  const out = new Array(faceLandmarks.length);
  for (let i = 0; i < faceLandmarks.length; i++) {
    const lm = faceLandmarks[i];
    out[i] = [lm.x * videoW, lm.y * videoH, (lm.z || 0) * zScale];
  }
  return out;
}

async function loadMediaPipeTasksFaceLandmarker({
  wasmBasePath = DEFAULT_TASKS_VISION_WASM_BASE,
  modelUrl = DEFAULT_FACE_LANDMARKER_MODEL_URL,
  maxFaces = 1,
  delegate = 'GPU',
} = {}) {
  const mod = await import('@mediapipe/tasks-vision');
  const { FaceLandmarker, FilesetResolver } = mod;

  // Use a dedicated canvas so we don't collide with Three.js' WebGL context.
  const mpCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : undefined;

  const vision = await FilesetResolver.forVisionTasks(wasmBasePath);
  const landmarker = await FaceLandmarker.createFromOptions(vision, {
    baseOptions: {
      modelAssetPath: modelUrl,
      delegate,
    },
    runningMode: 'VIDEO',
    numFaces: maxFaces,
    minFaceDetectionConfidence: 0.5,
    minFacePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputFaceBlendshapes: false,
    outputFacialTransformationMatrixes: false,
    ...(mpCanvas ? { canvas: mpCanvas } : {}),
  });

  return {
    kind: 'MediaPipeTasksFaceLandmarker',
    async estimateFaces({ input }) {
      const src = input;
      const videoW = src?.videoWidth || src?.width || src?.naturalWidth || 1;
      const videoH = src?.videoHeight || src?.height || src?.naturalHeight || 1;
      const t = performance?.now?.() ?? Date.now();
      const r = landmarker.detectForVideo(src, t);
      const first = r?.faceLandmarks?.[0];
      if (!first || first.length === 0) return [];
      const scaledMesh = toScaledMeshFromNormalizedLandmarks(first, videoW, videoH);
      return [{ scaledMesh }];
    },
    close() {
      try {
        landmarker?.close?.();
      } catch {
        // ignore
      }
    },
  };
}

async function loadTfjsFaceMesh({ maxFaces = 1, shouldLoadIrisModel = true } = {}) {
  // Dynamic import so TFJS doesn’t land in the initial bundle unless needed.
  const tf = await import('@tensorflow/tfjs-core');
  await import('@tensorflow/tfjs-converter');
  await import('@tensorflow/tfjs-backend-webgl');

  try {
    await tf.setBackend('webgl');
  } catch (e) {
    console.warn('[VTO] TFJS WebGL backend failed; falling back to CPU:', e);
    await tf.setBackend('cpu');
  }
  await tf.ready();

  const faceLandmarksDetection = await import('@tensorflow-models/face-landmarks-detection');
  const loadedModel = await faceLandmarksDetection.load(
    faceLandmarksDetection.SupportedPackages.mediapipeFacemesh,
    { shouldLoadIrisModel: !!shouldLoadIrisModel, maxFaces }
  );

  // Keep the instance: spreading it drops prototype methods such as
  // estimateFaces(), which breaks tracking when the TFJS fallback is used.
  loadedModel.kind = 'TfjsFaceMesh';
  return loadedModel;
}

export async function loadFaceTrackingModel({
  prefer = 'mediapipe',
  maxFaces = 1,
  mediapipe = {},
  tfjs = {},
} = {}) {
  if (prefer === 'mediapipe') {
    // Try delegates in order, then fallback to TFJS.
    // On mobile Safari, GPU delegate can conflict with Three.js WebGL contexts,
    // so callers can pass `mediapipe.delegates = ['CPU']` to force CPU.
    const delegates = Array.isArray(mediapipe.delegates) && mediapipe.delegates.length
      ? mediapipe.delegates
      : ['GPU', 'CPU'];
    let lastErr = null;
    for (const delegate of delegates) {
      try {
        // eslint-disable-next-line no-unused-vars
        const { delegates: _ignored, ...rest } = mediapipe || {};
        return await loadMediaPipeTasksFaceLandmarker({ ...rest, maxFaces, delegate });
      } catch (e) {
        lastErr = e;
        console.warn(`[VTO] MediaPipe Tasks (${delegate}) failed; trying next:`, e);
      }
    }
    console.warn('[VTO] MediaPipe Tasks failed; falling back to TFJS:', lastErr);
    return await loadTfjsFaceMesh({ maxFaces, ...tfjs });
  }

  return await loadTfjsFaceMesh({ maxFaces, ...tfjs });
}
